import { useEffect } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { BookOpen, Coins, TrendingUp, Users, Trophy, Wallet, Package, Info, Download } from "lucide-react";

/**
 * Admin Glossary — every short form used across dashboards, product tiles,
 * reports and rank ladders explained in plain English.
 * Route: /admin/glossary
 */

const SECTIONS = [
  {
    key: "value",
    title: "Value & Volume Units",
    icon: Coins,
    tone: "gold",
    rows: [
      {
        short: "SP",
        full: "Sales Point",
        detail: "Commission-math currency. Every product carries an SP tag (e.g. Noni Juice = SP 8). Every SP in your downline flows to the left or right leg — paired SP at the Friday closing unlocks payout ₹ per rank.",
      },
      {
        short: "SP",
        full: "Sales Point · formerly BV (Business Volume)",
        detail: "Binary-tree pairing unit. SP is credited to the buyer's placement side (Left or Right). At each weekly closing, matched SP pairs across legs generate the binary-matching income. Legacy label 'BV' is preserved in internal fields but the customer-facing terminology is SP everywhere.",
      },
      {
        short: "PV",
        full: "Point Value",
        detail: "Rank-qualification & bonus-tier unit. PV drives the 14-tier rank ladder (Silver → Crown) and the Monthly 2:1 Bonus tiers. Independent of price fluctuations.",
      },
      {
        short: "RV",
        full: "Rupee Value",
        detail: "Real-money turnover in ₹. Used for business-volume reports, tax calculations and admin dashboards. This is the actual cash your team generated.",
      },
      {
        short: "MRP",
        full: "Maximum Retail Price",
        detail: "Printed / customer-facing price of a product before any IBA discount.",
      },
      {
        short: "DP",
        full: "Distributor Price",
        detail: "IBA-only wholesale price — what IBAs pay to purchase the product for personal use or downline sale.",
      },
    ],
  },
  {
    key: "network",
    title: "Network / Sponsorship Terms",
    icon: Users,
    tone: "royal",
    rows: [
      {
        short: "Direct",
        full: "Direct Referral",
        detail: "A member YOU personally sponsored. Direct sales earn you Direct Referral Income (fixed % of DP).",
      },
      {
        short: "Indirect",
        full: "Indirect Referral / Downline",
        detail: "Members sponsored by anyone in your team below your Level-1 directs. Their volume flows to you as Group volume, matched pairs & Level income.",
      },
      {
        short: "PC",
        full: "Preferred Customer",
        detail: "A buyer who purchases via the shop but has NOT joined as an IBA. Their orders count towards YOUR turnover (Self/Group) without sponsoring commissions to them.",
      },
      {
        short: "IBA",
        full: "Independent Business Associate",
        detail: "An active member of your MLM — buys at DP, earns commissions, has a binary placement and a personal user code (e.g. SM100005).",
      },
      {
        short: "Sponsor",
        full: "Sponsor / Introducer",
        detail: "The IBA who invited a new member. Sponsorship determines Direct Referral Income — separate from tree placement (which controls binary matching).",
      },
      {
        short: "Placement",
        full: "Binary Tree Position",
        detail: "The Left / Right leg where a new IBA sits. Placement can differ from sponsorship — admins can manually move placement (audit-logged) without changing the sponsor.",
      },
    ],
  },
  {
    key: "period",
    title: "Time Period Labels",
    icon: TrendingUp,
    tone: "olive",
    rows: [
      {
        short: "Till Last Month",
        full: "Cumulative — Prior Months",
        detail: "Total value earned from account creation up to the end of the previous calendar month. Frozen — does not change during the current month.",
      },
      {
        short: "Current Month",
        full: "This Calendar Month",
        detail: "Value added between the 1st and today. Resets to zero on the 1st of every month.",
      },
      {
        short: "Accumulated",
        full: "Lifetime / All Time",
        detail: "Till Last Month + Current Month = total lifetime figure since joining. Used for rank qualifications & long-term reports.",
      },
    ],
  },
  {
    key: "rank",
    title: "Rank & Rewards",
    icon: Trophy,
    tone: "gold",
    rows: [
      {
        short: "Rank",
        full: "Leadership Rank",
        detail: "14-tier gemstone ladder — Executive → Sr. Executive → Silver → Gold → Pearl → Emerald → Sapphire → Ruby → Platinum (1,560 pairs) → Diamond → Royal → Crown-Ambassador → Imperial-Crown → Global-Crown.",
      },
      {
        short: "Matched Pair",
        full: "1:1 Binary Match",
        detail: "One pair = 1 SP on the Left leg + 1 SP on the Right leg. Pairs are computed at Friday closing and drive both binary income and rank progression.",
      },
      {
        short: "Trip Fund",
        full: "Leadership Trip Reward",
        detail: "Sponsored trip package earned at qualifying ranks (Silver = Domestic Trip, Emerald = Bangkok, Ruby = Dubai, Platinum = Europe, etc).",
      },
      {
        short: "2:1 Bonus",
        full: "Monthly 2:1 Matching Bonus",
        detail: "Tiered monthly reward when your higher leg is at least 2× the matching leg (e.g. Left 10,000 SP · Right 5,000 SP). Multiple tiers pay out ₹ amounts.",
      },
    ],
  },
  {
    key: "wallet",
    title: "Wallets & Payouts",
    icon: Wallet,
    tone: "royal",
    rows: [
      {
        short: "Shopping Wallet",
        full: "Product Purchase Wallet",
        detail: "Credit balance usable ONLY for buying Samara Wiz products (no cash withdrawal). Funded by direct referral income + SP bonuses.",
      },
      {
        short: "Reward Wallet",
        full: "Rank Reward Wallet",
        detail: "Cash-out wallet holding rank-milestone bonuses. Withdrawable to your bank account subject to KYC + weekly closing.",
      },
      {
        short: "Payout / UTR",
        full: "Weekly Payout / Bank Transfer",
        detail: "Every Friday closing, eligible balances are batched and paid out to KYC-verified bank accounts. UTR = Unique Transaction Reference from the bank.",
      },
      {
        short: "TDS",
        full: "Tax Deducted at Source",
        detail: "5% income tax deducted per Income Tax Act §194H on commission payouts. Reflected on every payout statement PDF.",
      },
    ],
  },
  {
    key: "product",
    title: "Product States",
    icon: Package,
    tone: "olive",
    rows: [
      {
        short: "LIVE",
        full: "Available for Sale",
        detail: "is_coming_soon = false. Product is bought/added to cart by public + IBAs.",
      },
      {
        short: "Coming Soon",
        full: "Not Yet Released",
        detail: "is_coming_soon = true. Product tile shows the Coming Soon placeholder until admin uploads a real photo AND flips the toggle. Tiles still visible so buyers can waitlist.",
      },
      {
        short: "Featured",
        full: "Homepage / Priority Product",
        detail: "is_featured = true. Bubbles to the top of the storefront grid and appears in featured tickers.",
      },
      {
        short: "Legacy",
        full: "Old Category Product",
        detail: "Products left over from an older category taxonomy (e.g. 'Beauty' before we canonicalized to 'Personal Care'). Auto-migrated on backend boot.",
      },
    ],
  },
];

const TONE_CLS = {
  gold:  { chip: "bg-gold/10 text-gold-deep border-gold/25",  head: "text-gold-deep" },
  royal: { chip: "bg-royal/10 text-royal border-royal/25",     head: "text-royal" },
  olive: { chip: "bg-olive/10 text-olive border-olive/25",     head: "text-olive" },
};

export default function AdminGlossary() {
  // Inject a print stylesheet that hides the app chrome (sidebar, header,
  // buttons) so `window.print()` yields a clean, trainer-ready PDF via the
  // browser's "Save as PDF" destination. No external libraries needed.
  useEffect(() => {
    const style = document.createElement("style");
    style.setAttribute("data-glossary-print", "");
    style.textContent = `
      @media print {
        @page { size: A4; margin: 14mm 12mm; }
        body { background: #ffffff !important; }
        [data-print-hide], nav, header, aside, .sidebar, [data-testid="admin-sidebar"] { display: none !important; }
        [data-print-root] { padding: 0 !important; margin: 0 !important; max-width: 100% !important; }
        [data-glossary-print-header] { display: block !important; }
        section[data-testid^="glossary-section-"] { break-inside: avoid; page-break-inside: avoid; box-shadow: none !important; border-color: #d4c9b0 !important; }
        article { break-inside: avoid; page-break-inside: avoid; }
        .bg-gradient-to-r, [data-testid="glossary-intro"] { background: #ffffff !important; color: #1a2332 !important; border: 1px solid #d4c9b0; }
        [data-testid="glossary-intro"] * { color: #1a2332 !important; }
      }
    `;
    document.head.appendChild(style);
    return () => { style.remove(); };
  }, []);

  const handleDownload = () => window.print();

  return (
    <DashboardLayout
      title="Glossary · Terms & Short Forms"
      subtitle="Every abbreviation used across product tiles, dashboards, reports and the rank ladder — explained in plain English."
      actions={
        <button
          type="button"
          data-testid="glossary-download-pdf"
          data-print-hide
          onClick={handleDownload}
          className="inline-flex items-center gap-2 bg-navy text-white px-4 py-2 rounded-sm hover:bg-navy-deep text-sm"
        >
          <Download size={14} /> Download PDF
        </button>
      }
    >
      <div className="max-w-5xl space-y-8" data-print-root>
        {/* Print-only cover header — hidden on-screen */}
        <div className="hidden" data-glossary-print-header>
          <h1 style={{ fontFamily: "serif", fontSize: 28, color: "#1a2332", margin: 0 }}>SAMARA WIZ — Direct Selling Glossary</h1>
          <p style={{ fontSize: 12, color: "#5c6675", marginTop: 6 }}>
            Terms & short forms used across product tiles, dashboards, reports and the rank ladder.
          </p>
          <hr style={{ borderColor: "#d4c9b0", marginTop: 12, marginBottom: 16 }} />
        </div>
        {/* Intro banner */}
        <div className="bg-gradient-to-r from-royal via-royal-deep to-navy text-white rounded-sm p-6 flex items-start gap-4" data-testid="glossary-intro">
          <div className="w-11 h-11 grid place-items-center bg-gold/20 border border-gold/40 rounded-sm shrink-0">
            <BookOpen size={22} className="text-gold" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-gold">Reference · v1</div>
            <h2 className="font-serif text-2xl mt-1">Direct Selling Dictionary</h2>
            <p className="text-sm text-white/80 mt-2 leading-relaxed">
              Share this page with new IBAs during on-boarding. Every term admins/franchises/IBAs
              see across the platform is defined here — no need to memorize the difference between
              SP (formerly BV), PV and RV.
            </p>
          </div>
        </div>

        {SECTIONS.map((sec) => {
          const Icon = sec.icon;
          const tone = TONE_CLS[sec.tone];
          return (
            <section
              key={sec.key}
              data-testid={`glossary-section-${sec.key}`}
              className="bg-white border border-line rounded-sm overflow-hidden"
            >
              <header className="px-5 py-3.5 border-b border-line bg-cream/40 flex items-center gap-2">
                <Icon size={14} className={tone.head} />
                <h3 className={`text-[11px] uppercase tracking-[0.3em] font-medium ${tone.head}`}>
                  {sec.title}
                </h3>
              </header>
              <div className="divide-y divide-line/70">
                {sec.rows.map((r) => (
                  <article
                    key={r.short}
                    data-testid={`glossary-${r.short.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                    className="px-5 py-4 grid grid-cols-1 md:grid-cols-[120px_180px_1fr] gap-3 md:gap-6 items-start"
                  >
                    <div>
                      <span className={`inline-block px-2.5 py-1 text-xs font-mono uppercase tracking-widest border rounded-sm ${tone.chip}`}>
                        {r.short}
                      </span>
                    </div>
                    <div className="font-serif text-base text-navy leading-snug">
                      {r.full}
                    </div>
                    <p className="text-sm text-navy/70 leading-relaxed">
                      {r.detail}
                    </p>
                  </article>
                ))}
              </div>
            </section>
          );
        })}

        <div className="text-[11px] text-navy/45 flex items-center gap-1.5 pt-2">
          <Info size={11} /> Definitions are aligned with SmartValue / Modicare-style compensation-plan conventions and Samara Wiz's official policy PDF.
        </div>
      </div>
    </DashboardLayout>
  );
}
